/**
 * User Service - V3 Schema with requiredHoursOverride
 *
 * Implements V3 User operations:
 * - createV3User(input): Create user with requiredHoursOverride
 * - getV3UserById(userId): Fetch user document
 * - updateV3User(userId, updates): Update user with validation
 * - getV3UsersByRole(role): Query users by role
 * - sanitizeV2UserToV3(v2User): Convert V2 user to V3 format
 *
 * Key V3 Changes:
 * - Removed: linkedPersonId (strict Actor/Dependent separation)
 * - Removed: annualHourOverrides (replaced by requiredHoursOverride)
 * - requiredHoursOverride supports signed half-hours from -999 to 999
 *
 * @see specs/datamodel-refactor/data-model.md for V3 schema specification
 */

import { normalizeEmail } from "@timebanken/utils"
import {
  type Firestore,
  type QueryDocumentSnapshot,
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore"

import type {
  V3User,
  V3UserCreateInput,
  V3UserUpdateInput,
} from "./types/v3-entities"
import type { MembershipStatus } from "./constants/statuses"
import { validateV3User } from "./types/v3-entities"
import {
  assertHourValue,
  assertOptionalHourValue,
  assertOptionalPhone,
  assertOptionalSpondId,
  definedFields,
  isBlank,
  isValidEmail,
} from "./v3-field-validation"

// ============================================================================
// Collection References
// ============================================================================

/**
 * Gets the users collection reference.
 * Storage: public/data/users
 */
export const getV3UsersCollectionRef = (db: Firestore) =>
  collection(db, "public/data/users")

const assertAccountEmailUnused = async (
  db: Firestore,
  email: string,
  excludedId?: string,
) => {
  const profiles = await getDocs(getV3UsersCollectionRef(db))
  if (profiles.docs.some((profile) => profile.id !== excludedId &&
    typeof profile.data().email === "string" &&
    normalizeEmail(profile.data().email) === normalizeEmail(email))) {
    throw new Error("Account email already exists; administrator review required")
  }
}

// ============================================================================
// User CRUD Operations
// ============================================================================

/**
 * Creates a new V3 User with requiredHoursOverride.
 *
 * @param db - Firestore database instance
 * @param input - User creation input
 * @param userId - Optional custom user ID (defaults to auto-generated)
 * @returns The user ID
 * @throws Error if validation fails
 */
export const createV3User = async (
  db: Firestore,
  input: V3UserCreateInput,
  userId?: string,
): Promise<string> => {
  if (isBlank(input.uid)) throw new Error("uid is required")
  if (isBlank(input.email)) throw new Error("email is required")
  if (!isValidEmail(normalizeEmail(input.email))) {
    throw new Error("Invalid email format")
  }
  assertHourValue(input.requiredHoursOverride, "requiredHoursOverride")
  // New writers use canonical membership values. Compatibility aliases remain readable.
  if (input.status && !["active", "inactive"].includes(input.status)) {
    throw new Error("status must be one of: active, inactive")
  }
  if (isBlank(input.role)) throw new Error("role is required")
  if (!Array.isArray(input.teams)) throw new Error("teams must be an array")
  assertOptionalPhone(input.phone)
  assertOptionalSpondId(input.spondId)

  await assertAccountEmailUnused(db, input.email)
  const now = Timestamp.now()
  const status = input.status || "active"
  const userData: Record<string, unknown> = {
    uid: input.uid.trim(),
    email: normalizeEmail(input.email),
    firstName: input.firstName?.trim() || "",
    lastName: input.lastName?.trim() || "",
    displayName:
      input.displayName?.trim() ||
      `${input.firstName || ""} ${input.lastName || ""}`.trim(),
    role: input.role.trim(),
    status,
    statusHistory: [{ status, changedAt: now }],
    teams: input.teams,
    requiredHoursOverride: input.requiredHoursOverride,
    createdAt: now,
    updatedAt: now,
    ...definedFields(input, ["phone", "lastSpondImportAt"]),
  }
  if (input.spondId !== undefined) userData.spondId = input.spondId.trim()

  // The document ID defaults to the Auth uid.
  const documentId = userId || input.uid
  await setDoc(doc(getV3UsersCollectionRef(db), documentId), userData)
  return documentId
}

/**
 * Gets a user by ID.
 *
 * @param db - Firestore database instance
 * @param userId - The user's document ID
 * @returns The user or null if not found
 */
export const getV3UserById = async (
  db: Firestore,
  userId: string,
): Promise<V3User | null> => {
  const userRef = doc(getV3UsersCollectionRef(db), userId)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    return null
  }

  return {
    id: userSnap.id,
    ...userSnap.data(),
  } as V3User
}

/**
 * Gets a user by Firebase Auth UID.
 *
 * @param db - Firestore database instance
 * @param uid - The Firebase Auth UID
 * @returns The user or null if not found
 */
export const getV3UserByUid = async (
  db: Firestore,
  uid: string,
): Promise<V3User | null> => {
  const usersSnapshot = await getDocs(
    query(getV3UsersCollectionRef(db), where("uid", "==", uid)),
  )

  if (usersSnapshot.empty) {
    return null
  }

  const userDoc = usersSnapshot.docs[0]
  return {
    id: userDoc.id,
    ...userDoc.data(),
  } as V3User
}

/**
 * Gets all users.
 *
 * @param db - Firestore database instance
 * @returns Array of all users
 */
export const getAllV3Users = async (db: Firestore): Promise<V3User[]> => {
  const usersSnapshot = await getDocs(query(getV3UsersCollectionRef(db)))
  return usersSnapshot.docs.map(
    (docSnap: QueryDocumentSnapshot) =>
      ({
        id: docSnap.id,
        ...docSnap.data(),
      }) as V3User,
  )
}

/**
 * Gets users by role.
 *
 * @param db - Firestore database instance
 * @param role - The role ID to filter by
 * @returns Array of users with the specified role
 */
export const getV3UsersByRole = async (
  db: Firestore,
  role: string,
): Promise<V3User[]> => {
  const usersSnapshot = await getDocs(
    query(getV3UsersCollectionRef(db), where("role", "==", role)),
  )
  return usersSnapshot.docs.map(
    (docSnap: QueryDocumentSnapshot) =>
      ({
        id: docSnap.id,
        ...docSnap.data(),
      }) as V3User,
  )
}

/**
 * Gets users by status.
 *
 * @param db - Firestore database instance
 * @param status - The status to filter by
 * @returns Array of users with the specified status
 */
export const getV3UsersByStatus = async (
  db: Firestore,
  status: MembershipStatus,
): Promise<V3User[]> => {
  const usersSnapshot = await getDocs(
    query(getV3UsersCollectionRef(db), where("status", "==", status)),
  )
  return usersSnapshot.docs.map(
    (docSnap: QueryDocumentSnapshot) =>
      ({
        id: docSnap.id,
        ...docSnap.data(),
      }) as V3User,
  )
}

/**
 * Gets users by team.
 *
 * @param db - Firestore database instance
 * @param teamId - The team ID to filter by
 * @returns Array of users in the specified team
 */
export const getV3UsersByTeam = async (
  db: Firestore,
  teamId: string,
): Promise<V3User[]> => {
  const usersSnapshot = await getDocs(
    query(
      getV3UsersCollectionRef(db),
      where("teams", "array-contains", teamId),
    ),
  )
  return usersSnapshot.docs.map(
    (docSnap: QueryDocumentSnapshot) =>
      ({
        id: docSnap.id,
        ...docSnap.data(),
      }) as V3User,
  )
}

/**
 * Updates a user.
 *
 * @param db - Firestore database instance
 * @param userId - The user's document ID
 * @param updates - Partial user data to update
 */
export const updateV3User = async (
  db: Firestore,
  userId: string,
  updates: V3UserUpdateInput,
): Promise<void> => {
  assertOptionalHourValue(
    updates.requiredHoursOverride,
    "requiredHoursOverride",
  )

  if (updates.email !== undefined) {
    if (!isValidEmail(normalizeEmail(updates.email))) {
      throw new Error("Invalid email format")
    }
    updates.email = normalizeEmail(updates.email)
    await assertAccountEmailUnused(db, updates.email, userId)
  }

  if (updates.status !== undefined) {
    throw new Error(
      "Membership status must be changed with updateUserMembershipStatus",
    )
  }

  if (updates.role !== undefined && updates.role.trim() === "") {
    throw new Error("role cannot be empty")
  }
  assertOptionalPhone(updates.phone)
  assertOptionalSpondId(updates.spondId)

  // Validate teams if being updated
  if (updates.teams !== undefined && !Array.isArray(updates.teams)) {
    throw new Error("teams must be an array")
  }

  const {
    status: _status,
    statusHistory: _statusHistory,
    ...nonStatusUpdates
  } = updates

  if (Object.keys(nonStatusUpdates).length === 0) {
    return
  }

  const userRef = doc(getV3UsersCollectionRef(db), userId)
  await updateDoc(userRef, {
    ...nonStatusUpdates,
    updatedAt: Timestamp.now(),
  })
}

/**
 * Deletes a user.
 *
 * @param db - Firestore database instance
 * @param userId - The user's document ID
 */
export const deleteV3User = async (
  db: Firestore,
  userId: string,
): Promise<void> => {
  const userRef = doc(getV3UsersCollectionRef(db), userId)
  await deleteDoc(userRef)
}

/**
 * Validates a V3 User object.
 * Re-export from v3-entities for convenience.
 */
export { validateV3User }

// ============================================================================
// Required Hours Override Helpers
// ============================================================================

/**
 * Updates a user's requiredHoursOverride.
 *
 * @param db - Firestore database instance
 * @param userId - The user's document ID
 * @param hours - The new requiredHoursOverride value
 * @throws Error if hours is out of range
 */
export const updateV3UserRequiredHoursOverride = async (
  db: Firestore,
  userId: string,
  hours: number,
): Promise<void> => {
  assertHourValue(hours, "requiredHoursOverride")

  const userRef = doc(getV3UsersCollectionRef(db), userId)
  await updateDoc(userRef, {
    requiredHoursOverride: hours,
    updatedAt: Timestamp.now(),
  })
}

/**
 * Clears a user's requiredHoursOverride (resets to 0).
 *
 * @param db - Firestore database instance
 * @param userId - The user's document ID
 */
export const clearV3UserRequiredHoursOverride = async (
  db: Firestore,
  userId: string,
): Promise<void> => {
  const userRef = doc(getV3UsersCollectionRef(db), userId)
  await updateDoc(userRef, {
    requiredHoursOverride: 0,
    updatedAt: Timestamp.now(),
  })
}

// ============================================================================
// Role Management Helpers
// ============================================================================

/**
 * Updates a user's role.
 *
 * @param db - Firestore database instance
 * @param userId - The user's document ID
 * @param roleId - The new role ID
 */
export const updateV3UserRole = async (
  db: Firestore,
  userId: string,
  roleId: string,
): Promise<void> => {
  if (!roleId || roleId.trim() === "") {
    throw new Error("roleId cannot be empty")
  }

  const userRef = doc(getV3UsersCollectionRef(db), userId)
  await updateDoc(userRef, {
    role: roleId.trim(),
    updatedAt: Timestamp.now(),
  })
}

// ============================================================================
// Team Management Helpers
// ============================================================================

/**
 * Adds a team to a user's teams array.
 *
 * @param db - Firestore database instance
 * @param userId - The user's document ID
 * @param teamId - The team ID to add
 */
export const addTeamToV3User = async (
  db: Firestore,
  userId: string,
  teamId: string,
): Promise<void> => {
  const user = await getV3UserById(db, userId)
  if (!user) {
    throw new Error(`User with ID ${userId} not found`)
  }

  const currentTeams = user.teams || []
  const userWithTeamIds = user as unknown as { teamIds?: unknown }
  const currentTeamIds = Array.isArray(userWithTeamIds.teamIds)
    ? userWithTeamIds.teamIds
    : []

  if (currentTeams.includes(teamId) || currentTeamIds.includes(teamId)) {
    return // Already in team
  }

  const userRef = doc(getV3UsersCollectionRef(db), userId)
  await updateDoc(userRef, {
    teams: [...currentTeams, teamId],
    teamIds: [...currentTeamIds, teamId],
    updatedAt: Timestamp.now(),
  })
}

/**
 * Removes a team from a user's teams array.
 *
 * @param db - Firestore database instance
 * @param userId - The user's document ID
 * @param teamId - The team ID to remove
 */
export const removeTeamFromV3User = async (
  db: Firestore,
  userId: string,
  teamId: string,
): Promise<void> => {
  const user = await getV3UserById(db, userId)
  if (!user) {
    throw new Error(`User with ID ${userId} not found`)
  }

  const userRef = doc(getV3UsersCollectionRef(db), userId)
  const currentTeams = user.teams || []
  const userWithTeamIds = user as unknown as { teamIds?: unknown }
  const currentTeamIds = Array.isArray(userWithTeamIds.teamIds)
    ? userWithTeamIds.teamIds
    : []
  await updateDoc(userRef, {
    teams: currentTeams.filter((t) => t !== teamId),
    teamIds: currentTeamIds.filter((t) => t !== teamId),
    updatedAt: Timestamp.now(),
  })
}

// ============================================================================
// GDPR Consent Helpers
// ============================================================================

/**
 * Updates a user's phone sharing consent (GDPR).
 *
 * Updates both `phoneShareConsent` and `phoneShareConsentUpdatedAt` atomically.
 * Uses serverTimestamp() for the timestamp to ensure server-authoritative time.
 *
 * @param db - Firestore database instance
 * @param userId - The user's document ID (must match authenticated user)
 * @param consent - Whether the user consents to sharing (true/false)
 * @throws Error if userId is empty
 */
export const updatePhoneShareConsent = async (
  db: Firestore,
  userId: string,
  consent: boolean,
): Promise<void> => {
  if (!userId || userId.trim() === "") {
    throw new Error("userId is required")
  }

  const userRef = doc(getV3UsersCollectionRef(db), userId)
  await updateDoc(userRef, {
    phoneShareConsent: consent,
    phoneShareConsentUpdatedAt: serverTimestamp(),
  })
}

// ============================================================================
// Migration Helpers
// ============================================================================

/**
 * V2 User structure (for migration reference).
 * Contains legacy fields that are removed in V3.
 */
export interface V2User {
  id: string
  uid: string
  email: string
  firstName?: string
  lastName?: string
  displayName?: string
  role: string
  status?: "active" | "inactive"
  teams?: string[]
  /** @deprecated Removed in V3 - strict Actor/Dependent separation */
  linkedPersonId?: string
  /** @deprecated Removed in V3 - replaced by requiredHoursOverride */
  annualHourOverrides?: { [year: string]: number }
  createdAt?: Timestamp
  updatedAt?: Timestamp
}

/**
 * Sanitizes a V2 User to V3 format.
 * Removes legacy fields and adds new required fields.
 *
 * @param v2User - The V2 user object
 * @returns A V3-compliant user object
 */
export const sanitizeV2UserToV3 = (v2User: V2User): V3User => {
  // Extract non-legacy fields
  const {
    linkedPersonId: _linkedPersonId, // Remove
    annualHourOverrides: _annualHourOverrides, // Remove
    ...cleanUser
  } = v2User

  return {
    id: cleanUser.id,
    uid: cleanUser.uid,
    email: cleanUser.email,
    firstName: cleanUser.firstName || "",
    lastName: cleanUser.lastName || "",
    displayName:
      cleanUser.displayName ||
      `${cleanUser.firstName || ""} ${cleanUser.lastName || ""}`.trim(),
    role: cleanUser.role || "guardian",
    status: cleanUser.status || "active",
    teams: cleanUser.teams || [],
    requiredHoursOverride: 0, // Default for migrated users
    createdAt: cleanUser.createdAt || Timestamp.now(),
    updatedAt: Timestamp.now(),
  }
}

/**
 * Checks if a user object is V3 compliant.
 *
 * @param user - User object to check
 * @returns true if V3 compliant, false otherwise
 */
export const isV3User = (user: Record<string, unknown>): boolean => {
  // Must NOT have legacy fields
  if ("linkedPersonId" in user || "annualHourOverrides" in user) {
    return false
  }

  // Must have requiredHoursOverride
  if (!("requiredHoursOverride" in user)) {
    return false
  }

  // Validate using the entity validator
  return validateV3User(user as Partial<V3User>)
}
